import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'next-i18next'
import { Card } from 'ui/src/components/Card/Card'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { ResizablePanel } from 'ui/src/components/ResizeablePanel'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { useSelectedTypeOfContractAtom } from '@/components/ProductData/ProductDataProvider'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { Features } from '@/utils/Features'
import { useFormatter } from '@/utils/useFormatter'
import { CardRadioGroup } from '../CardRadioGroup/CardRadioGroup'
import { textTransition, vars } from '../CardRadioGroup/CardRadioGroup.css'
import { InfoButton } from '../InfoButton/InfoButton'
import { NonCoverageInfoDialogContent } from '../NonCoverageInfoDialogContent'
import { TierComparisonDialogContent } from '../TierComparisonDialogContent'
import { TierCoverageInfoDialogContent } from './TierCoverageInfoDialogContent/TierCoverageInfoDialogContent'

type Props = {
  offers: Array<ProductOfferFragment>
  selectedOffer: ProductOfferFragment
  onValueChange: (offerId: string) => void
}

export function ProductTierSelectorV2({ offers, selectedOffer, onValueChange }: Props) {
  const formatter = useFormatter()
  const setTypeOfContract = useSetAtom(useSelectedTypeOfContractAtom())
  const { t } = useTranslation('purchase-form')

  const onOpenCoverageDialog = () => {
    setTypeOfContract(selectedOffer.variant.typeOfContract)
  }

  return (
    <>
      <CardRadioGroup.Root value={selectedOffer.id} onValueChange={onValueChange}>
        {offers.map((offer) => {
          const isSelected = selectedOffer.id === offer.id

          return (
            <LargeDialog.Root key={offer.id}>
              <CardRadioGroup.Item className={yStack({ gap: 'md' })} value={offer.id}>
                <Card.Aside>
                  <CardRadioGroup.Badge isSelected={isSelected}>
                    {formatter.monthlyPrice(offer.cost.net)}
                  </CardRadioGroup.Badge>
                </Card.Aside>
                <Card.Header>
                  <Card.Title as="h2" className={xStack({ alignItems: 'center', gap: 'xxs' })}>
                    {offer.product.categoryDisplayNameShort ? (
                      <>
                        <Text as="span" style={{ color: vars.titleColor }} className={textTransition} size="xl">
                          {offer.product.categoryDisplayNameShort}
                        </Text>
                        <Text as="span" color="textSecondary" size="xl">
                          {offer.variant.displayNameSubtype}
                        </Text>
                      </>
                    ) : (
                      <Text as="span" style={{ color: vars.titleColor }} className={textTransition} size="xl">
                        {offer.variant.displayNameSubtype}
                      </Text>
                    )}
                  </Card.Title>
                </Card.Header>
                <ResizablePanel>
                  <Card.Content className={yStack({ gap: 'md' })}>
                    {offer.variant.tierDescription && (
                      <Text className={textTransition} style={{ color: vars.descriptionColor }}>
                        {offer.variant.tierDescription}
                      </Text>
                    )}
                    {Features.enabled('TIER_USPS') && isSelected && offer.usps.length > 0 && (
                      <ul className={yStack({ gap: 'xs' })}>
                        {offer.usps.map((usp) => (
                          <li key={usp} className={xStack({ gap: 'xs', alignItems: 'flex-start' })}>
                            <CheckIcon
                              size="1rem"
                              color={vars.descriptionColor}
                              // margin top is applied to visually align the icon with the text
                              className={clsx(sprinkles({ marginTop: 'xxs' }), textTransition)}
                            />
                            <Text className={textTransition}>{usp}</Text>
                          </li>
                        ))}
                      </ul>
                    )}

                    <ConditionalWrapper
                      condition={isSelected}
                      wrapWith={(children) => (
                        <Dialog.Trigger onClick={onOpenCoverageDialog} asChild>
                          {children}
                        </Dialog.Trigger>
                      )}
                    >
                      <CardRadioGroup.Button
                        className={sprinkles({ mt: 'xs' })}
                        variant="secondary"
                        size="medium"
                        fullWidth
                      >
                        {isSelected
                          ? t('TIER_COVERAGE_BUTTON')
                          : t('TIER_SELECT_BUTTON', { tierName: offer.variant.displayNameSubtype })}
                      </CardRadioGroup.Button>
                    </ConditionalWrapper>
                  </Card.Content>
                </ResizablePanel>
              </CardRadioGroup.Item>

              {offers.length > 1 && (
                <TierCoverageInfoDialogContent
                  title={offer.variant.displayNameSubtype || offer.variant.displayName}
                  subtitle={t('TIER_COVERAGE_DIALOG_SUBTITLE')}
                  selectedOffer={offer}
                />
              )}

              {offers.length === 1 && <NonCoverageInfoDialogContent />}
            </LargeDialog.Root>
          )
        })}
      </CardRadioGroup.Root>

      {offers.length > 1 && (
        <LargeDialog.Root>
          <Dialog.Trigger asChild>
            <InfoButton>
              <div className={yStack()}>
                <Text as="div" size="xs">
                  {t('COMPARE_COVERAGE_DIALOG_TRIGGER')}
                </Text>
                <Text as="span" color="textSecondary" size="xs" balance={true}>
                  {t('COMPARE_COVERAGE_DIALOG_TRIGGER_DESCRIPTION')}
                </Text>
              </div>
            </InfoButton>
          </Dialog.Trigger>
          <TierComparisonDialogContent
            title={t('COMPARE_TIERS_DIALOG_TITLE')}
            subtitle={t('COMPARE_TIERS_DIALOG_SUBTITLE')}
            showCoverageText={true}
          />
        </LargeDialog.Root>
      )}
    </>
  )
}
